import { Component, h, Event, EventEmitter, Prop } from "@stencil/core";
import { SelectOptionChangedEvent } from "@apis/model/form-events";
import { SelectProps } from "./f-select-model";
import { generateRandomNumber } from "@globals/random-number-helpers";

@Component({
  tag: "f-select",
  styleUrl: "f-select.scss",
  shadow: true,
})
export class Select {
  @Prop() variant: SelectProps["variant"] = "default";
  @Prop() propertyId: SelectProps["propertyId"] =
    "f-select-" + generateRandomNumber();
  @Prop() name: SelectProps["name"];
  @Prop() label: SelectProps["label"];
  @Prop() hiddenLabel: SelectProps["hiddenLabel"] = false;
  @Prop() floatingLabel: SelectProps["floatingLabel"] = true;
  @Prop() mandatory?: SelectProps["mandatory"];
  @Prop() dynamicWidth: SelectProps["dynamicWidth"] = false;
  @Prop({ mutable: true }) disabled: SelectProps["disabled"] = false;
  @Prop() isValid: () => boolean;
  @Prop() options: SelectProps["options"] = [];
  /* TTRQ-53602 custom start */
  @Prop() isLightTheme: boolean;
  /* TTRQ-53602 custom end */

  @Event({ eventName: "selectOptionChanged" })
  optionChanged: EventEmitter<SelectOptionChangedEvent>;

  componentWillLoad() {
    if (typeof this.options === "string") {
      this.options = JSON.parse(this.options);
    }
  }

  private handleChange = (event: Event): void => {
    const selectedIndex = (event.target as HTMLSelectElement).selectedIndex;
    const selectedValue = (event.target as HTMLSelectElement).value;

    this.optionChanged.emit({
      fieldId: this.propertyId,
      selectedOptionIndex: selectedIndex,
      selectedOptionValue: selectedValue,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
    });
  };

  private labelVersion = {
    isHidden: () => Boolean(this.label) && Boolean(this.hiddenLabel),
    isFloating: () =>
      Boolean(this.label) &&
      !Boolean(this.hiddenLabel) &&
      Boolean(this.floatingLabel),
    isNotFloating: () =>
      Boolean(this.label) &&
      !Boolean(this.hiddenLabel) &&
      !Boolean(this.floatingLabel),
  };

  private generateLabel = (hidden = false) => {
    if (hidden) {
      return (
        <f-visually-hidden>
          <label htmlFor={this.propertyId} data-test="select-label-hidden">
            {this.label}
          </label>
        </f-visually-hidden>
      );
    }

    return (
      <label
        htmlFor={this.propertyId}
        class="a-dropdown__label"
        data-test="select-label"
      >
        {`${this.label}${this.mandatory ? "*" : ""}`}
      </label>
    );
  };

  render() {
    return (
      <div
        class={{
          "a-dropdown": true,
          "a-dropdown--disabled": this.disabled,
          "a-dropdown--dynamic-width": this.dynamicWidth,
          "a-dropdown--floating-label": this.labelVersion.isFloating(),
          "a-dropdown--small": this.variant === "small",
          "light-theme": this.isLightTheme, //TTRQ-53602 custom
        }}
        data-test="select-wrapper"
      >
        {this.labelVersion.isHidden() && this.generateLabel(true)}
        {this.labelVersion.isNotFloating() && this.generateLabel()}
        <div class="a-dropdown__select-wrapper">
          {this.labelVersion.isFloating() && this.generateLabel()}
          <select
            class="a-dropdown__select"
            name={this.name}
            id={this.propertyId}
            onChange={this.handleChange}
            disabled={this.disabled}
            data-test="select"
          >
            {typeof this.options !== "string" &&
              this.options.map((option) => {
                return (
                  <option
                    key={`option-${option.value}`}
                    value={`${option.value}`}
                    selected={option.initiallySelected}
                    data-test="select-option"
                  >
                    {option.name}
                  </option>
                );
              })}
          </select>
        </div>
      </div>
    );
  }
}
